import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RouterIcon from '@mui/icons-material/Router';

import { api } from '../services/api';

// ─── helpers ────────────────────────────────────────────────────────────────

const isJnet2 = (d) => {
  if (!d?.capabilities) return false;
  const compatible = (d.compatible || d.capabilities?.compatible || '').toLowerCase();
  if (compatible && compatible !== 'jnet2') return false;
  const model = (d.capabilities?.model || '').toLowerCase();
  if (model.includes('nvidia') || model.includes('jetson') || model.includes('qrb')) return false;
  return true;
};

const isCap = (d) => {
  if (!d?.capabilities) return false;
  const role = (d.capabilities?.role || '').toLowerCase();
  return role === 'cap' || role === 'gateway';
};

const candidateSerial = (c) =>
  (c.AP_NodeID || c.ap_node_id || '').toLowerCase().replace(/:/g, '');

const candidateRSSI = (snr, nf) => {
  const s = snr ?? 0;
  const n = nf ?? 0;
  return s + n;
};

// ─── Candidate detail panel ──────────────────────────────────────────────────

const CandidateDetailPanel = ({ device, data, deviceLookup }) => {
  if (!data) return null;

  const selectedRankId = data.selected_rank_id ?? data.selectedRankId ?? -1;
  const candidates = [...(data.candidates || [])].sort((a, b) => {
    const rankA = a.rank_idx ?? a.RankIdx ?? 0;
    const rankB = b.rank_idx ?? b.RankIdx ?? 0;
    // Selected candidate always first, then by rank ascending
    const aSelected = rankA === selectedRankId;
    const bSelected = rankB === selectedRankId;
    if (aSelected !== bSelected) return aSelected ? -1 : 1;
    return rankA - rankB;
  });

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          TM Candidates for&nbsp;
          <Box component="span" sx={{ fontFamily: 'monospace' }}>
            {device.name || device.serial_number}
          </Box>
        </Typography>
        {candidates.length > 0 && (
          <Chip size="small" label={`${candidates.length} candidate${candidates.length !== 1 ? 's' : ''}`} />
        )}
        {selectedRankId >= 0 && (
          <Chip
            size="small"
            color="success"
            icon={<CheckCircleIcon />}
            label={`Selected rank ${selectedRankId}`}
          />
        )}
      </Stack>

      {candidates.length === 0 ? (
        <Alert severity="info">No candidates found for this device.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Candidate device</TableCell>
                <TableCell align="right">AP RSSI (dBm)</TableCell>
                <TableCell align="right">STA RSSI (dBm)</TableCell>
                <TableCell align="right">Channel</TableCell>
                <TableCell align="right">Hop dist (m)</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidates.map((c, idx) => {
                const rankIdx = c.rank_idx ?? c.RankIdx ?? 0;
                const isSelected = rankIdx === selectedRankId;
                const serial = candidateSerial(c);
                const candidateDev = deviceLookup.get(serial);
                const apRSSI = candidateRSSI(c.AP_SNR ?? c.ap_snr, c.AP_NF ?? c.ap_nf);
                const staRSSI = candidateRSSI(c.STA_SNR ?? c.sta_snr, c.STA_NF ?? c.sta_nf);
                const channel = c.Channel ?? c.channel ?? '—';
                const hopDist = c.HOP_Distance ?? c.hop_distance ?? '—';

                return (
                  <TableRow
                    key={idx}
                    sx={{
                      backgroundColor: isSelected
                        ? (t) => (t.palette.mode === 'dark'
                          ? 'rgba(76,175,80,0.18)'
                          : 'rgba(76,175,80,0.10)')
                        : undefined,
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {isSelected
                          ? <CheckCircleIcon fontSize="small" color="success" />
                          : <RadioButtonUncheckedIcon fontSize="small" color="disabled" />}
                        <Typography variant="body2" fontWeight={isSelected ? 700 : 400}>
                          {rankIdx}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {serial || '—'}
                        </Typography>
                        {candidateDev && (
                          <Typography variant="caption" color="text.secondary">
                            {candidateDev.name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={apRSSI >= -70 ? 'success.main' : apRSSI >= -85 ? 'warning.main' : 'error.main'}
                        fontWeight={500}
                      >
                        {apRSSI} dBm
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={staRSSI >= -70 ? 'success.main' : staRSSI >= -85 ? 'warning.main' : 'error.main'}
                        fontWeight={500}
                      >
                        {staRSSI} dBm
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{channel}</TableCell>
                    <TableCell align="right">
                      {typeof hopDist === 'number' ? `${Math.round(hopDist)} m` : hopDist}
                    </TableCell>
                    <TableCell>
                      {isSelected ? (
                        <Chip size="small" color="success" label="Selected" />
                      ) : (
                        <Chip size="small" color="default" variant="outlined" label="Candidate" />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ─── Device list tab ─────────────────────────────────────────────────────────

const DeviceListTab = ({ devices, openedSerials, onOpen, deviceLookup }) => {
  const [filter, setFilter] = useState('');

  const eligible = useMemo(
    () => (devices || []).filter((d) => isJnet2(d) && !isCap(d) && d.connected),
    [devices],
  );

  const rows = useMemo(() => {
    const trimmed = filter.trim().toLowerCase();
    return trimmed
      ? eligible.filter(
          (d) =>
            d.serial_number?.toLowerCase().includes(trimmed) ||
            d.name?.toLowerCase().includes(trimmed),
        )
      : eligible;
  }, [eligible, filter]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-start' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            TM Candidate Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a connected jnet2 Node to view its topology-management parent candidates.
            The TM algorithm ranks each candidate by RF quality; the active (selected) parent
            is highlighted at the top.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" color="primary" label={`${eligible.length} eligible`} />
          <Chip size="small" color="success" variant="outlined" label={`${eligible.filter(d => d.connected).length} connected`} />
        </Stack>
      </Stack>

      <TextField
        size="small"
        placeholder="Filter by serial or name"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Device</TableCell>
              <TableCell>Current parent</TableCell>
              <TableCell>Connection</TableCell>
              <TableCell align="right">View candidates</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No eligible devices match the current filter.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((d) => {
              const serial = d.serial_number?.toLowerCase();
              const isOpen = openedSerials.has(serial);

              // Resolve current parent from device data
              const parentSerial = d.parent_serial?.toLowerCase();
              const parentDev = parentSerial ? deviceLookup.get(parentSerial) : null;
              const parentLabel = parentDev
                ? (parentDev.name || parentDev.serial_number)
                : (parentSerial || null);

              return (
                <TableRow
                  key={serial}
                  hover
                  sx={{
                    backgroundColor: isOpen
                      ? (t) => (t.palette.mode === 'dark'
                        ? 'rgba(33,150,243,0.12)'
                        : 'rgba(33,150,243,0.06)')
                      : undefined,
                  }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <RouterIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {d.name || d.serial_number}
                        </Typography>
                        {d.name && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {d.serial_number}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {parentLabel ? (
                      <Box>
                        <Typography variant="body2">{parentLabel}</Typography>
                        {parentDev?.name && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {parentSerial}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={d.connected ? 'success' : 'default'}
                      label={d.connected ? 'Connected' : 'Disconnected'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={isOpen ? 'Already open — click tab to view' : 'Open TM candidates in a new tab'}>
                      <span>
                        <Button
                          size="small"
                          variant={isOpen ? 'contained' : 'outlined'}
                          color="secondary"
                          startIcon={<AccountTreeIcon fontSize="small" />}
                          onClick={() => onOpen(d)}
                          disabled={isOpen}
                        >
                          {isOpen ? 'Open' : 'View'}
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ─── Main container ──────────────────────────────────────────────────────────

const TmCandidatesTab = ({ devices }) => {
  // deviceTabs: array of { device, data: null | TmReport, loading, error }
  const [deviceTabs, setDeviceTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('__list__');

  const deviceLookup = useMemo(() => {
    const map = new Map();
    (devices || []).forEach((d) => {
      if (d?.serial_number) map.set(d.serial_number.toLowerCase(), d);
    });
    return map;
  }, [devices]);

  const openedSerials = useMemo(
    () => new Set(deviceTabs.map((t) => t.device.serial_number?.toLowerCase())),
    [deviceTabs],
  );

  const handleOpen = useCallback(async (device) => {
    const serial = device.serial_number?.toLowerCase();
    if (openedSerials.has(serial)) {
      setActiveTab(serial);
      return;
    }

    // Add tab in loading state and switch to it
    setDeviceTabs((prev) => [
      ...prev,
      { device, data: null, loading: true, error: null },
    ]);
    setActiveTab(serial);

    try {
      const data = await api.getTmReport(serial);
      setDeviceTabs((prev) =>
        prev.map((t) =>
          t.device.serial_number?.toLowerCase() === serial
            ? { ...t, data, loading: false }
            : t,
        ),
      );
    } catch (err) {
      setDeviceTabs((prev) =>
        prev.map((t) =>
          t.device.serial_number?.toLowerCase() === serial
            ? { ...t, loading: false, error: err?.message || 'Failed to load TM report' }
            : t,
        ),
      );
    }
  }, [openedSerials]);

  const handleClose = useCallback((serial, e) => {
    e?.stopPropagation();
    setDeviceTabs((prev) => {
      const next = prev.filter((t) => t.device.serial_number?.toLowerCase() !== serial);
      // If we closed the active tab, fall back to the list or the previous tab
      if (activeTab === serial) {
        if (next.length > 0) {
          setActiveTab(next[next.length - 1].device.serial_number?.toLowerCase());
        } else {
          setActiveTab('__list__');
        }
      }
      return next;
    });
  }, [activeTab]);

  const handleRetry = useCallback(async (device) => {
    const serial = device.serial_number?.toLowerCase();
    setDeviceTabs((prev) =>
      prev.map((t) =>
        t.device.serial_number?.toLowerCase() === serial
          ? { ...t, loading: true, error: null }
          : t,
      ),
    );
    try {
      const data = await api.getTmReport(serial);
      setDeviceTabs((prev) =>
        prev.map((t) =>
          t.device.serial_number?.toLowerCase() === serial
            ? { ...t, data, loading: false }
            : t,
        ),
      );
    } catch (err) {
      setDeviceTabs((prev) =>
        prev.map((t) =>
          t.device.serial_number?.toLowerCase() === serial
            ? { ...t, loading: false, error: err?.message || 'Failed to load TM report' }
            : t,
        ),
      );
    }
  }, []);

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      {/* Tab bar */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: '0.85rem' },
          }}
        >
          {/* Static "Devices" list tab */}
          <Tab
            value="__list__"
            label="Devices"
            icon={<RouterIcon fontSize="small" />}
            iconPosition="start"
          />

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

          {/* One tab per opened device */}
          {deviceTabs.map((t) => {
            const serial = t.device.serial_number?.toLowerCase();
            const label = t.device.name || t.device.serial_number;
            const selectedRankId = t.data?.selected_rank_id ?? t.data?.selectedRankId ?? -1;
            const candidateCount = t.data?.candidates?.length ?? 0;
            return (
              <Tab
                key={serial}
                value={serial}
                icon={
                  t.loading
                    ? <CircularProgress size={14} />
                    : <AccountTreeIcon fontSize="small" />
                }
                iconPosition="start"
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>{label}</span>
                    {!t.loading && !t.error && candidateCount > 0 && (
                      <Chip
                        size="small"
                        label={candidateCount}
                        sx={{ height: 16, fontSize: '0.65rem', pointerEvents: 'none' }}
                      />
                    )}
                    {!t.loading && !t.error && selectedRankId >= 0 && (
                      <CheckCircleIcon fontSize="small" color="success" sx={{ ml: 0.25 }} />
                    )}
                    <IconButton
                      size="small"
                      onClick={(e) => handleClose(serial, e)}
                      sx={{ ml: 0.25, p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Stack>
                }
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Panels */}
      <Box role="tabpanel" hidden={activeTab !== '__list__'}>
        {activeTab === '__list__' && (
          <DeviceListTab
            devices={devices}
            openedSerials={openedSerials}
            onOpen={handleOpen}
            deviceLookup={deviceLookup}
          />
        )}
      </Box>

      {deviceTabs.map((t) => {
        const serial = t.device.serial_number?.toLowerCase();
        return (
          <Box key={serial} role="tabpanel" hidden={activeTab !== serial}>
            {activeTab === serial && (
              <>
                {t.loading && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                    <Typography color="text.secondary">Loading TM candidates…</Typography>
                  </Box>
                )}
                {t.error && (
                  <Alert
                    severity="error"
                    action={
                      <Button size="small" color="inherit" onClick={() => handleRetry(t.device)}>
                        Retry
                      </Button>
                    }
                  >
                    {t.error}
                  </Alert>
                )}
                {!t.loading && !t.error && (
                  <CandidateDetailPanel
                    device={t.device}
                    data={t.data}
                    deviceLookup={deviceLookup}
                  />
                )}
              </>
            )}
          </Box>
        );
      })}
    </Paper>
  );
};

export { CandidateDetailPanel };

export default TmCandidatesTab;
